import { Popover, Spin, Typography } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownIcon } from "src/assets/svgs";
import { CustomButton } from "src/components/ui";
import { getLanguageHeaders } from "src/services/commonLanguageHeader";
import { ServicesUrlEndpoints } from "src/services/servicesEndpointConstant";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import ApiService from "src/util/api.service";
import { getFromLocal, removeFromLocal } from "src/util/cache.service";
import { LOCAL_KEYS, USER_ROLE } from "src/util/util.constant";
import { cn } from "src/util/cn";
import DistributorSettingsModal from "./DistributorSettingsModal";

const DEFAULT_DISTRIBUTOR_ID = 1;
const ArrowDownIconComponent = ArrowDownIcon as unknown as ComponentType<{
  className?: string;
}>;

interface UserDetail {
  role?: string | string[];
  roles?: string | string[];
  user?: {
    role?: string | string[];
    roles?: string | string[];
  };
}

interface Distributor {
  id: number;
  name: string;
}

interface DistributorListResponse {
  response?: Distributor[];
}

const HIGH_LEVEL_ROLES = [
  USER_ROLE.SuperUser,
  USER_ROLE.RootrUser,
  USER_ROLE.CRMAdmin,
  USER_ROLE.CRMUser,
  "RootrUser",
].map((role) =>
  String(role)
    .toLowerCase()
    .replace(/[\s_-]/g, "")
);

const normalizeRole = (role: unknown): string =>
  role
    ? String(role)
        .toLowerCase()
        .replace(/[\s_-]/g, "")
    : "";

const getRoleCandidates = () => {
  const localRole = getFromLocal(LOCAL_KEYS.ROLE, false, true);
  const localUserDetail = getFromLocal<UserDetail>(LOCAL_KEYS.USER_DETAIL, true, true);

  return [localRole, localUserDetail?.role, localUserDetail?.user?.role, localUserDetail?.roles]
    .flat()
    .filter(Boolean);
};

const getPersistedDistributorId = (): number | null => {
  const selectedDistributor = getFromLocal<string | number>(
    LOCAL_KEYS.SELECTED_DISTRIBUTOR,
    false,
    true
  );

  if (selectedDistributor && selectedDistributor !== "null") {
    return Number(selectedDistributor);
  }

  const persistedDistributor = localStorage.getItem("persist:distributor");
  if (!persistedDistributor) return null;

  try {
    const parsedDistributor = JSON.parse(persistedDistributor);
    const distributorId = JSON.parse(parsedDistributor?.distributorId ?? null);
    return distributorId && distributorId !== "null" ? Number(distributorId) : null;
  } catch (error) {
    console.error("Failed to parse persisted distributor:", error);
    return null;
  }
};

const getAuthToken = (): string | null => getFromLocal<string>(LOCAL_KEYS.TOKEN, false, true);

const getDistributorList = async (): Promise<DistributorListResponse> => {
  const token = getAuthToken();
  const languageHeaders = getLanguageHeaders();

  const response = await ApiService.get(
    ServicesUrlEndpoints.USER_LEVEL_ACCESS.LOOKUP.DISTRIBUTOR_LIST,
    {
      headers: {
        ...languageHeaders.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  return response.data as DistributorListResponse;
};

interface HighLevelUserAccessProps {
  privacyPolicyBlocking: boolean;
}

const HighLevelUserAccess = ({ privacyPolicyBlocking }: HighLevelUserAccessProps) => {
  const { i18n, t } = useTranslation();

  const canSelectDistributor = getRoleCandidates().some((role) =>
    HIGH_LEVEL_ROLES.includes(normalizeRole(role))
  );

  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { selectedDistributor, setSelectedDistributor } = useSelectDistributor();

  const { data: distributorListData, isSuccess: isDistributorSuccess } = useQuery({
    queryKey: ["distributorList", i18n.language],
    queryFn: getDistributorList,
    enabled: canSelectDistributor,
  });

  useEffect(() => {
    if (!canSelectDistributor || privacyPolicyBlocking) {
      setOpen(false);
      return;
    }

    const shouldOpenOnLogin = getFromLocal<string>(
      LOCAL_KEYS.SHOW_DISTRIBUTOR_POPOVER,
      false,
      false
    );

    if (shouldOpenOnLogin === "true") {
      setOpen(true);
      removeFromLocal(LOCAL_KEYS.SHOW_DISTRIBUTOR_POPOVER, false);
    }
  }, [canSelectDistributor, privacyPolicyBlocking]);

  useEffect(() => {
    if (!canSelectDistributor) return;

    const persistedDistributorId = getPersistedDistributorId();
    if (persistedDistributorId) {
      setSelectedDistributor(persistedDistributorId);
      return;
    }

    setSelectedDistributor(DEFAULT_DISTRIBUTOR_ID);
  }, [canSelectDistributor, setSelectedDistributor]);

  useEffect(() => {
    if (!canSelectDistributor || selectedDistributor || !isDistributorSuccess) return;

    const firstDistributor = distributorListData?.response?.[0];
    if (!firstDistributor?.id) return;

    setSelectedDistributor(firstDistributor.id);
  }, [
    distributorListData,
    isDistributorSuccess,
    canSelectDistributor,
    selectedDistributor,
    setSelectedDistributor,
  ]);

  const distributors = useMemo(
    () => distributorListData?.response ?? [],
    [distributorListData?.response]
  );

  const currentDistributor = useMemo(
    () =>
      distributors.find((distributor) => String(distributor.id) === String(selectedDistributor)),
    [distributors, selectedDistributor]
  );

  const currentDistributorName = (currentDistributor?.name ?? "SIPLI").toUpperCase();

  const handleDistributorChange = useCallback(
    (value: number) => {
      setSelectedDistributor(value);
      setOpen(false);
    },
    [setSelectedDistributor]
  );

  const handleDistributorSettingsClick = useCallback(() => {
    setOpen(false);
    setSettingsOpen(true);
  }, []);

  const content = useMemo(
    () => (
      <div className="w-[238px] select-none overflow-hidden rounded-b-[22px] bg-white">
        <div className="border-b border-gray5 p-4">
          <Typography className="m-0! text-lg! font-bold! leading-6! text-black!">
            {t("distributor")}
          </Typography>
        </div>

        <div className="border-b border-gray5">
          <div className="px-4 pt-4 pb-2 text-base font-bold leading-5 text-black">
            {t("switch_distributor")}
          </div>
          <div className="max-h-[120px] overflow-y-auto" role="radiogroup">
            {!isDistributorSuccess ? (
              <div className="flex h-22 items-center justify-center">
                <Spin size="small" />
              </div>
            ) : (
              distributors.map((distributor) => {
                const isSelected = String(distributor.id) === String(selectedDistributor);

                return (
                  <button
                    key={distributor.id}
                    type="button"
                    className={cn(
                      "flex h-[35px] w-full items-center gap-3 border-0 border-b border-solid border-gray3 px-4 text-left text-base leading-5 transition-colors last:border-b-0",
                      isSelected
                        ? "bg-[#edf2ff] font-bold text-primary"
                        : "bg-white font-normal text-black hover:bg-[#f7f9ff]"
                    )}
                    aria-checked={isSelected}
                    role="radio"
                    onClick={() => handleDistributorChange(distributor.id)}
                  >
                    <span
                      className={cn(
                        "relative h-4 w-4 shrink-0 rounded-full border border-solid",
                        isSelected ? "border-primary" : "border-gray3"
                      )}
                    >
                      {isSelected && (
                        <span className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary" />
                      )}
                    </span>
                    <span className="truncate text-sm">{distributor.name}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="p-4">
          <CustomButton
            type="secondary"
            buttonSize="medium"
            className="flex w-full items-center justify-center rounded px-4 py-2 text-sm font-bold leading-none"
            label={t("distributor_settings")}
            onClick={handleDistributorSettingsClick}
            disabled={!selectedDistributor}
          />
        </div>
      </div>
    ),
    [
      distributors,
      handleDistributorChange,
      handleDistributorSettingsClick,
      isDistributorSuccess,
      selectedDistributor,
      t,
    ]
  );

  if (!canSelectDistributor) return null;

  return (
    <>
      <Popover
        content={content}
        trigger="click"
        open={open}
        onOpenChange={(nextOpen) => !privacyPolicyBlocking && setOpen(nextOpen)}
        placement="bottomRight"
        align={{ offset: [0, 7] }}
        overlayClassName="distributor-switcher-popover"
      >
        <button
          type="button"
          className="flex h-[51px] min-w-[136px] max-w-[240px] items-center justify-between border-0 bg-gray1 px-4 py-2 text-left"
        >
          <span className="min-w-0 grow">
            <span className="block text-xs font-normal leading-5 text-gray8">
              {t("distributor")}
            </span>
            <span className="block truncate whitespace-nowrap text-sm font-bold leading-5 text-black">
              {currentDistributorName}
            </span>
          </span>
          <ArrowDownIconComponent className="ml-3 h-[14px] w-[14px] shrink-0 [&_path]:fill-gray8" />
        </button>
      </Popover>

      <DistributorSettingsModal
        open={settingsOpen}
        distributorId={selectedDistributor}
        distributorName={currentDistributor?.name ?? currentDistributorName}
        onClose={() => setSettingsOpen(false)}
      />
    </>
  );
};

export default HighLevelUserAccess;
